/* eslint-disable */
// /src/services/notificationsService.js
// Notifications Service (DH Mobility)
// ✅ SSOT 저장 위치: members/{memberDocId}/notifications/{nid}
// ✅ 뱃지 카운트(옵션): members/{memberDocId}.unreadNotiCount (increment)
//
// 포함 기능
// 1) 관리자: 주문 상태 변경 알림 생성(createOrderStatusNotificationForOwner)
// 2) 사용자: 내 알림 리스트 조회(listMyNotifications)
// 3) 사용자: 알림 읽음 처리(markMyNotificationRead) + unread 카운트 감소
// 4) 사용자: unread 카운트 구독(subscribeMyUnreadCount)  ← 배지용(허용 범위)

import {
    addDoc,
    collection,
    doc,
    getDoc,
    getDocs,
    increment,
    limit as qlimit,
    onSnapshot,
    orderBy,
    query,
    serverTimestamp,
    updateDoc,
    where,
} from "firebase/firestore";

import { db } from "./firebase";



function safeStr(v) {
    return String(v == null ? "" : v).trim();
}

/* ===========================
   Session helpers (Web)
   - DH Mobility 프로젝트에서 쓰는 localStorage: "dhm.session"
=========================== */
function getCurrentSessionSync() {
    try {
        return JSON.parse(localStorage.getItem("dhm.session") || "null");
    } catch {
        return null;
    }
}

function extractPhoneE164FromSession(sess) {
    if (!sess) return "";

    const v =
        sess.phoneE164 ||
        sess.user?.phoneE164 ||
        sess.member?.phoneE164 ||
        sess.profile?.phoneE164 ||
        sess.phone ||
        "";

    return safeStr(v);
}

/* ===========================
   Member lookup
   - members where phoneE164 == ...
   - return docId (first)
=========================== */
async function findMemberDocIdByPhoneE164(phoneE164) {
    const p = safeStr(phoneE164);
    if (!p) return "";

    try {
        const colRef = collection(db, "members");
        const q1 = query(colRef, where("phoneE164", "==", p), qlimit(1));
        const snap = await getDocs(q1);
        if (snap.empty) return "";
        return snap.docs[0]?.id || "";
    } catch (e) {
        console.warn("[notificationsService] findMemberDocIdByPhoneE164 failed", e);
        return "";
    }
}

/* ===========================
   Public: resolve my member doc id
   - 캐시: dhm.memberDocId.v1 (선택)
=========================== */
export async function resolveMyMemberDocId() {
    const sess = getCurrentSessionSync();
    const phoneE164 = extractPhoneE164FromSession(sess);
    if (!phoneE164) return "";

    const cacheKey = "dhm.memberDocId.v1";
    try {
        const cached = safeStr(localStorage.getItem(cacheKey));
        if (cached) return cached;
    } catch { }

    const docId = await findMemberDocIdByPhoneE164(phoneE164);
    if (docId) {
        try {
            localStorage.setItem(cacheKey, docId);
        } catch { }
    }
    return docId;
}

/* =========================================================
   1) ADMIN: create order status notification (SSOT)
   - 대상: ownerPhoneE164 (members 문서 기준)
========================================================= */
/**
 * @param {Object} params
 * @param {string} params.ownerPhoneE164
 * @param {string} params.orderId
 * @param {string} params.prevStatus
 * @param {string} params.nextStatus
 * @param {string} params.kind   - "STORE_ORDER_STATUS" | "FRANCHISE_ORDER_STATUS"
 * @param {string} [params.title]
 * @param {string} [params.body]
 * @param {boolean} [params.bumpUnread] - 기본 true
 */
export async function createOrderStatusNotificationForOwner(params = {}) {
    const ownerPhoneE164 = safeStr(params.ownerPhoneE164);
    const orderId = safeStr(params.orderId);
    const prevStatus = safeStr(params.prevStatus);
    const nextStatus = safeStr(params.nextStatus);
    const kind = safeStr(params.kind);

    if (!ownerPhoneE164 || !orderId || !nextStatus || !kind) {
        return { ok: false, skipped: true, reason: "missing_required_fields" };
    }

    const memberDocId = await findMemberDocIdByPhoneE164(ownerPhoneE164);
    if (!memberDocId) {
        return { ok: false, skipped: true, reason: "member_not_found" };
    }

    const title = safeStr(params.title) || "주문 상태 변경";
    const body =
        safeStr(params.body) || `주문 상태가 '${nextStatus}'로 변경되었습니다.`;

    try {
        const notifColRef = collection(db, "members", memberDocId, "notifications");

        const docRef = await addDoc(notifColRef, {
            type: kind,
            orderId,
            prevStatus,
            status: nextStatus,
            title,
            body,
            read: false,
            createdAt: serverTimestamp(),
        });

        const bumpUnread =
            typeof params.bumpUnread === "boolean" ? params.bumpUnread : true;

        if (bumpUnread) {
            try {
                await updateDoc(doc(db, "members", memberDocId), {
                    unreadNotiCount: increment(1),
                    lastNotiAt: serverTimestamp(),
                });
            } catch (e2) {
                console.warn("[notificationsService] bump unreadNotiCount failed", e2);
            }
        }

        return { ok: true, memberDocId, notificationId: docRef?.id || "" };
    } catch (e) {
        console.error(
            "[notificationsService] createOrderStatusNotificationForOwner failed",
            e
        );
        return { ok: false, error: e?.message || "create_notification_failed" };
    }
}

/* =========================================================
   2) USER: list my notifications (1회 조회)
========================================================= */
export async function listMyNotifications({ limitCount = 50 } = {}) {
    const memberDocId = await resolveMyMemberDocId();
    if (!memberDocId) return [];

    try {
        const colRef = collection(db, "members", memberDocId, "notifications");
        const q1 = query(colRef, orderBy("createdAt", "desc"), qlimit(limitCount));
        const snap = await getDocs(q1);

        return snap.docs.map((d) => {
            const data = d.data() || {};
            return { id: d.id, ...data };
        });
    } catch (e) {
        console.warn("[notificationsService] listMyNotifications failed", e);
        return [];
    }
}

/* =========================================================
   3) USER: mark one notification as read
   - read=true, readAt set
   - unreadNotiCount 감소(중복 감소 방지)
========================================================= */
export async function markMyNotificationRead(notificationId) {
    const memberDocId = await resolveMyMemberDocId();
    if (!memberDocId) return { ok: false, reason: "member_not_found" };

    const notifId = safeStr(notificationId);
    if (!notifId) return { ok: false, reason: "missing_notification_id" };

    const notifRef = doc(db, "members", memberDocId, "notifications", notifId);

    try {
        const snap = await getDoc(notifRef);
        if (!snap.exists()) return { ok: false, reason: "notification_not_found" };
        const cur = snap.data() || {};
        if (cur.read === true) return { ok: true, skipped: true };
    } catch (e) {
        console.warn("[notificationsService] mark read precheck failed", e);
    }

    try {
        await updateDoc(notifRef, { read: true, readAt: serverTimestamp() });
    } catch (e2) {
        console.warn("[notificationsService] update notification read failed", e2);
        return { ok: false, error: e2?.message || "update_read_failed" };
    }

    try {
        await updateDoc(doc(db, "members", memberDocId), {
            unreadNotiCount: increment(-1),
            lastNotiReadAt: serverTimestamp(),
        });
    } catch (e3) {
        console.warn("[notificationsService] decrement unreadNotiCount failed", e3);
    }

    return { ok: true };
}

/* =========================================================
   4) USER: subscribe unread count (badge)
   - members/{docId}.unreadNotiCount 구독
   - 반환: unsubscribe()
========================================================= */
export function subscribeMyUnreadCount(onValue) {
    let unsub = null;

    (async () => {
        const memberDocId = await resolveMyMemberDocId();
        if (!memberDocId) {
            try {
                onValue && onValue(0);
            } catch { }
            return;
        }

        const ref1 = doc(db, "members", memberDocId);
        unsub = onSnapshot(
            ref1,
            (snap) => {
                const data = snap.data() || {};
                const n = Number(data.unreadNotiCount || 0);
                try {
                    onValue && onValue(Number.isFinite(n) ? n : 0);
                } catch { }
            },
            (err) => {
                console.warn("[notificationsService] subscribeMyUnreadCount failed", err);
                try {
                    onValue && onValue(0);
                } catch { }
            }
        );
    })();

    return () => {
        try {
            unsub && unsub();
        } catch { }
    };
}
